<script setup lang="ts">
import { useId } from 'vue'
import type { SockTypeImageOption } from '../sockTypeImages'

withDefaults(defineProps<{
  modelValue: string
  options: SockTypeImageOption[]
  title: string
  name: string
  required?: boolean
  error?: string
}>(), {
  required: false,
  error: '',
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
}>()

const errorId = `sock-type-grid-error-${useId().replaceAll(':', '')}`

function select(value: string): void {
  emit('update:modelValue', value)
}
</script>

<template>
  <fieldset
    class="sock-picker"
    :class="{ 'sock-picker--invalid': !!error }"
    :aria-describedby="error ? errorId : undefined"
  >
    <legend class="sock-picker__legend">
      {{ title }}<span v-if="required" class="sock-picker__required" aria-hidden="true"> *</span>
    </legend>
    <div class="sock-picker__grid">
      <label
        v-for="option in options"
        :key="option.value"
        class="sock-picker__card"
        :class="{ 'is-selected': modelValue === option.value }"
      >
        <input
          class="sock-picker__input"
          type="radio"
          :name="name"
          :value="option.value"
          :checked="modelValue === option.value"
          @change="select(option.value)"
        >
        <img
          class="sock-picker__image"
          :src="option.image"
          alt=""
          width="64"
          height="64"
          loading="lazy"
          decoding="async"
        >
        <span class="sock-picker__label">{{ option.label }}</span>
      </label>
    </div>
    <small v-if="error" :id="errorId" class="sock-picker__error">{{ error }}</small>
  </fieldset>
</template>

<style scoped>
.sock-picker {
  display: grid;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.sock-picker__legend {
  padding: 0;
  color: #53625d;
  font-size: 13px;
  font-weight: 700;
}

.sock-picker__required {
  color: #852d22;
}

.sock-picker__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(76px, 88px));
  gap: 6px;
  justify-content: start;
}

.sock-picker__card {
  position: relative;
  display: grid;
  gap: 3px;
  align-content: start;
  justify-items: center;
  padding: 4px 4px 6px;
  border: 1px solid #d5ddda;
  background: #fff;
  cursor: pointer;
}

.sock-picker__card:hover {
  border-color: #7ba2cc;
  background: #f4f8fc;
}

.sock-picker__card:focus-within {
  outline: 2px solid #3568a8;
  outline-offset: 2px;
}

.sock-picker__card.is-selected {
  border-color: #3568a8;
  background: #e7f0fb;
  box-shadow: inset 0 0 0 1px #3568a8;
}

.sock-picker__card.is-selected::after {
  position: absolute;
  top: 2px;
  right: 2px;
  display: grid;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  color: #fff;
  background: #3568a8;
  content: "✓";
  font-size: 10px;
  font-weight: 800;
  place-items: center;
}

.sock-picker__input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.sock-picker__image {
  display: block;
  width: 64px;
  height: 64px;
  object-fit: contain;
  border: 1px solid #eef2f0;
  background: #fff;
}

.sock-picker__label {
  color: #173454;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}

.sock-picker__card.is-selected .sock-picker__label {
  color: #17457d;
}

.sock-picker--invalid .sock-picker__grid {
  outline: 1px solid #d7a8a1;
  outline-offset: 3px;
}

.sock-picker__error {
  color: #852d22;
  font-size: 12px;
  font-weight: 600;
}

@media (max-width: 620px) {
  .sock-picker__grid {
    grid-template-columns: repeat(auto-fill, minmax(68px, 80px));
  }
}
</style>
